import { X } from "lucide-react";
import { DownloadIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { GeneratedImageItem } from "@packages/api/madlenAPI.schemas";
import { Button } from "@core/button";

const ImagePreviewModal = ({
  images,
  setIsModalOpen,
}: {
  images: GeneratedImageItem[];
  setIsModalOpen: (isModalOpen: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<number | null>(0);
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedImage(null);
  }, [setIsModalOpen]);

  const navigateImage = useCallback(
    (direction: "next" | "prev") => {
      if (selectedImage === null || !images?.length) return;

      const maxIndex = images.length - 1;
      let newIndex;

      if (direction === "next") {
        newIndex = selectedImage < maxIndex ? selectedImage + 1 : 0;
      } else {
        newIndex = selectedImage > 0 ? selectedImage - 1 : maxIndex;
      }

      setSelectedImage(newIndex);
    },
    [selectedImage, images],
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
        e.preventDefault();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        navigateImage("prev");
        e.preventDefault();
      } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        navigateImage("next");
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleClose, navigateImage]);

  // Touch gestures
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;

    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > 50) {
      // Minimum swipe distance
      if (diff > 0) {
        navigateImage("next");
      } else {
        navigateImage("prev");
      }
    }
    setTouchStart(null);
  };

  const handleDownload = async (imageUrl: string | undefined) => {
    if (!imageUrl) return;
    setIsLoading(true);
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `generated-image-${Date.now()}.jpg`;
    link.click();
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  // Focus trap
  useEffect(() => {
    const modalElement = modalRef.current;
    if (!modalElement) return;

    const focusableElements = modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const firstFocusable = focusableElements[0] as HTMLElement;
    const lastFocusable = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    firstFocusable?.focus();

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable?.focus();
        }
      }
    };

    modalElement.addEventListener("keydown", handleTabKey);
    return () => modalElement.removeEventListener("keydown", handleTabKey);
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black/80"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      ref={modalRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative flex flex-col bg-white dark:bg-gray-800 rounded-2xl w-full max-w-7xl h-[90vh]">
        <Button
          variant="outline"
          size="icon"
          rounded="full"
          className="inline-flex m-4 z-[9999] shadow-lg hover:shadow-xl ml-auto"
          bgColor="bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700"
          textColor="text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          borderColor="border-gray-200 dark:border-gray-600"
          leftIcon={<X size={28} />}
          onClick={handleClose}
          aria-label={t("common.close")}
        />
        <h2 id="modal-title" className="sr-only">
          {t("common.imagePreview")}
        </h2>
        {/* Main content area with overflow handling */}
        <div className="flex-1 min-h-0 p-3 sm:p-4 overflow-y-auto">
          <div className="flex flex-col md:flex-row gap-4 h-full">
            {/* Main large image */}
            {selectedImage !== null && (
              <div className="relative flex-1 md:w-2/3 h-full flex items-center justify-center group">
                <div className="relative w-full h-full max-h-[calc(80vh-120px)]">
                  <img
                    src={images[selectedImage]?.imageUrl}
                    alt={`Generated image ${selectedImage + 1}`}
                    className="w-full h-full object-contain rounded-xl"
                  />

                  {/* Navigation buttons */}
                  {images.length > 1 && (
                    <>
                      <div className="absolute inset-y-0 left-0 flex items-center">
                        <Button
                          variant="outline"
                          size="icon"
                          rounded="full"
                          className="transform -translate-x-1/2 sm:translate-x-0 shadow-lg hover:shadow-xl group-hover:opacity-100 opacity-60 sm:opacity-100"
                          bgColor="bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700"
                          textColor="text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                          borderColor="border-gray-200 dark:border-gray-600"
                          leftIcon={<ChevronLeft size={28} />}
                          onClick={() => navigateImage("prev")}
                          aria-label={t("common.previousImage")}
                        />
                      </div>
                      <div className="absolute inset-y-0 right-0 flex items-center">
                        <Button
                          variant="outline"
                          size="icon"
                          rounded="full"
                          className="transform translate-x-1/2 sm:translate-x-0 shadow-lg hover:shadow-xl group-hover:opacity-100 opacity-60 sm:opacity-100"
                          bgColor="bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700"
                          textColor="text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                          borderColor="border-gray-200 dark:border-gray-600"
                          leftIcon={<ChevronRight size={28} />}
                          onClick={() => navigateImage("next")}
                          aria-label={t("common.nextImage")}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="w-full md:w-1/3 flex md:flex-col gap-2 overflow-x-auto md:overflow-y-auto p-2">
                {images?.map((image, index) => (
                  <Button
                    variant="ghost"
                    rounded="lg"
                    className={`relative shrink-0 h-40 w-20 sm:w-24 md:w-full overflow-hidden p-0 ${
                      selectedImage === index
                        ? "ring-2 ring-lime-500"
                        : "hover:opacity-80"
                    }`}
                    onClick={() => setSelectedImage(index)}
                    aria-label={`Select image ${index + 1}`}
                    aria-pressed={selectedImage === index}
                  >
                    <img
                      src={image.imageUrl}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Footer with download button - fixed at bottom */}
        <div className="flex justify-end gap-2 p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <Button
            variant="solid"
            size="md"
            rounded="lg"
            bgColor="bg-lime-500 hover:bg-lime-600"
            textColor="text-white"
            leftIcon={<DownloadIcon size={20} />}
            disabled={isLoading}
            className={isLoading ? "opacity-50 cursor-not-allowed" : ""}
            onClick={() => {
              if (images && selectedImage !== null) {
                const image = images[selectedImage];
                if (image) {
                  handleDownload(image.imageUrl);
                }
              }
            }}
            aria-busy={isLoading}
          >
            {t("common.download")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImagePreviewModal;
